import axios from 'axios';
import { generateTraceId, getOrCreateSessionId } from './traceIds';
import { getNetworkLogSnapshot } from './networkLog';
import { getBreadcrumbsSnapshot } from './breadcrumbs';
import { getConsoleLogSnapshot } from './consoleLog';
import { captureScreenshot } from './screenshot';
import { isBrowserEnvironment } from './environment';

type CaptureContext = {
  componentStack?: string;
  axiosError?: unknown;
  route?: string;
  method?: string;
  statusCode?: number;
  durationMs?: number;
  traceId?: string;
};

type FrontendCapturePayload = {
  traceId: string;
  sessionId: string;
  severity: 'Error' | 'Warning' | 'Info';
  url: string;
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  referrerUrl: string;
  locale: string;
  exception?: {
    type: string;
    message: string;
    stackTrace?: string;
    source?: string;
  };
  stackFrames?: Array<{ function?: string; file?: string; line?: number; column?: number }>;
  breadcrumbs: ReturnType<typeof getBreadcrumbsSnapshot>;
  networkLog: ReturnType<typeof getNetworkLogSnapshot>;
  consoleLog: ReturnType<typeof getConsoleLogSnapshot>;
  screenshotDataUrl?: string;
  screenshotFailureReason?: string;
  route?: string;
  method?: string;
  statusCode?: number;
  durationMs?: number;
};

const capturedErrors = new WeakSet<object>();

export async function captureFrontendError(
  error: unknown,
  context: CaptureContext = {}
): Promise<void> {
  if (!isBrowserEnvironment()) return;
  try {
    if (error && typeof error === 'object') {
      if (capturedErrors.has(error)) return;
      capturedErrors.add(error);
    }

    const traceId = context.traceId || generateTraceId();
    const sessionId = getOrCreateSessionId();

    const { type, message, stackTrace, source } = extractException(error);

    const screenshot = await captureScreenshot();

    const payload: FrontendCapturePayload = {
      traceId,
      sessionId,
      severity: 'Error',
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      referrerUrl: document.referrer,
      locale: navigator.language,
      exception: { type, message, stackTrace, source },
      breadcrumbs: getBreadcrumbsSnapshot(),
      networkLog: getNetworkLogSnapshot(),
      consoleLog: getConsoleLogSnapshot(),
      screenshotDataUrl: screenshot.base64 || undefined,
      screenshotFailureReason: screenshot.failureReason,
      route: context.route,
      method: context.method,
      statusCode: context.statusCode,
      durationMs: context.durationMs,
    };

    await axios.post('/api/observability/capture', payload);
  } catch {
  }
}

function extractException(err: unknown): {
  type: string;
  message: string;
  stackTrace?: string;
  source?: string;
} {
  if (err instanceof Error) {
    return {
      type: err.name || 'Error',
      message: err.message,
      stackTrace: err.stack,
    };
  }
  if (typeof err === 'object' && err !== null) {
    const anyErr = err as { message?: string; stack?: string; name?: string; response?: { status?: number; data?: unknown } };
    if (anyErr.response) {
      return {
        type: 'AxiosError',
        message: anyErr.message || 'Axios request failed',
        stackTrace: anyErr.stack,
        source: `status=${anyErr.response.status}`,
      };
    }
    return {
      type: anyErr.name || 'Error',
      message: anyErr.message || String(err),
      stackTrace: anyErr.stack,
    };
  }
  return { type: 'UnknownError', message: String(err) };
}
